import React, { useEffect, useState } from 'react';
import SyncIcon from '@mui/icons-material/Sync';
import GlobalWrapper from '../../../components/Wrapper/GlobalWrapper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import GlobalDatagrid from '../../../components/mui/DataGrid/GlobalDatagrid';
import ActionDetails from '../../../components/mui/GlobalAnchor/ActionDetails';
import GlobalAnchorList from '../../../components/mui/GlobalAnchor/GlobalAnchorList';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { getSessionPreferences, getSessionPreferencesSync } from '../../../redux/features/croSlice';
import { CRO_SESSION_PREFERENCE_TABLE } from '../../../components/datagrid/CRO/CroSessionPreference';
import AddSessionPreference from './AddSessionPreference';
import DeleteSessionPreference from './DeleteSessionPreference';
import { useLocation, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { savePaginationData } from '../../../redux/features/globalDatagridSlice';
import EditSessionPreference from './UpdateSessionPreference';
import useHandleApiResponse from '../../../Hooks/useHandleApiResponse';
import CircularProgress from '@mui/material/CircularProgress';
import GlobalDialog from '../../../components/mui/Dialogue/GlobalDialog';
import WarningPopup from '../myworkspace/CROleads/components/WarningPopup';

const SessionPreferences = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const courseenrollmentId = searchParams.get('courseenrollmentId');
  const studentname = searchParams.get('studentname');
  const course = searchParams.get('course');
  const phone = searchParams.get('phone');
  // opened from the retention workspace (stage 4): go back there once a preference is added. Only workspace paths.
  const returnTo = (searchParams.get('returnTo') || '').startsWith('/student-journey/') ? searchParams.get('returnTo') : null;
  const navigate = useNavigate();
  const [warning, setWarning] = useState(false);

  const dispatch = useDispatch();
  const { paginationData } = useSelector((state) => state.globalDataGrid);
  const { gridSize, pageNum, rowData, openAnchor, search } = paginationData;
  const loading = useSelector((state) => state.cro.loading);
  const SessionPreferences = useSelector((state) => state.cro.SessionPreferences);
  const syncLoading = useSelector((state) => state.cro.syncLoading);
  const apiHelperHandle = useHandleApiResponse();

  const [value, setValue] = useState(dayjs());

  const data = {
    page: pageNum,
    limit: gridSize,
    search: search,
    subject_enrollment_id: '',
    course_enrollment_id: courseenrollmentId,
  };

  useEffect(() => {
    if (pageNum && gridSize) {
      dispatch(
        savePaginationData({
          ...paginationData,
          openAnchor: false,
        }),
      );
      dispatch(getSessionPreferences(data));
    }
  }, [pageNum, gridSize, search]);

  return (
    <GlobalWrapper
      title={'Session Preferences'}
      actions={
        <Stack direction="row" spacing={1}>
          {returnTo && (
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(returnTo)}>
              Back to workspace
            </Button>
          )}
          <AddSessionPreference
            dataparams={data}
            courseenrollmentId={courseenrollmentId}
            onAdded={returnTo ? () => navigate(returnTo) : undefined}
          />
        </Stack>
      }
    >
      <Stack
        direction={{
          xs: 'column',
          sm: 'row',
        }}
        spacing={1}
        sx={{ marginBottom: 2, justifyContent: 'space-between' }}
      >
        <Stack
          direction={{
            xs: 'column',
            sm: 'row',
          }}
          spacing={1}
        >
          <Box
            sx={{
              backgroundImage: 'linear-gradient(to right,#1E91D6,#141B41)',
              px: '20px',
              py: '10px',
              borderRadius: '15px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '16px',
            }}
          >
            Name : {studentname || SessionPreferences?.data?.[0]?.lead_name}
          </Box>
          <Box
            sx={{
              backgroundImage: 'linear-gradient(to right,#1E91D6,#141B41)',
              px: '20px',
              py: '10px',
              borderRadius: '15px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '16px',
            }}
          >
            Phone Number : {phone || SessionPreferences?.data?.[0]?.lead_mob_no}
          </Box>
          <Box
            sx={{
              backgroundImage: 'linear-gradient(to right,#1E91D6,#141B41)',
              px: '20px',
              py: '10px',
              borderRadius: '15px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '16px',
            }}
          >
            Course : {course || SessionPreferences?.data?.[0]?.course_name}
          </Box>
        </Stack>
        <Button
          onClick={() => {
            setWarning(true);
          }}
          color="info"
          startIcon={syncLoading ? <CircularProgress color="primary" size={'20px'} /> : <SyncIcon />}
          variant="contained"
          disabled={syncLoading}
        >
          {syncLoading ? 'Syncing...' : 'Sync'}
        </Button>
      </Stack>
      <GlobalDialog
        title={'Warning'}
        content={
          <WarningPopup
            handleSubmit={() => {
              dispatch(
                getSessionPreferencesSync({
                  subject_enrollment_id: SessionPreferences.data?.[0].subject_enrollment_id,
                }),
              ).then((res) => apiHelperHandle(res, { onSuccess: () => setWarning(false) }));
            }}
            values={warning}
            loading={syncLoading}
          />
        }
        setOpen={setWarning}
        open={Boolean(warning)}
        closeButton
      />

      <Box sx={{ width: '100%', padding: 0 }}>
        <GlobalDatagrid
          tableHead={CRO_SESSION_PREFERENCE_TABLE}
          rows={SessionPreferences.data}
          meta={SessionPreferences?.meta?.totalRecords || 0}
          rowUniqueId="sl_no"
          loading={loading}
          rowClick={true}
          actionComponent={
            <>
              <ActionDetails val={rowData?.id} />
              <GlobalAnchorList component={<DeleteSessionPreference data={data} id={rowData?.id} />} label="" />
              <GlobalAnchorList component={<EditSessionPreference data={data} rowData={rowData} />} label="Update" />
            </>
          }
        />
      </Box>
    </GlobalWrapper>
  );
};

export default SessionPreferences;
